import {
  CURRENT_VERSION,
  DEFAULT_CALCULATION_RECEIPT,
  DEFAULT_PACKAGE_RECOMMENDATION,
  DEFAULT_REGIONAL_SETTINGS,
  DEFAULT_SEASONAL_DEMAND,
  FunnelWizardStateSchema,
  type FunnelWizardState,
} from "@/lib/funnel-planner/schema";
import { buildInitialGraph } from "@/lib/funnel-planner/graph-utils";

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function versionFromState(state: unknown): number | null {
  if (!isRecord(state) || typeof state.version !== "number") return null;
  return Number.isInteger(state.version) ? state.version : null;
}

function migrateV1ToV2(state: unknown): unknown {
  if (!isRecord(state)) return state;
  const stages = Array.isArray(state.stages)
    ? state.stages.map((stage: unknown) => {
        if (!isRecord(stage)) return stage;
        return { ...stage, stageType: stage.stageType ?? "custom" };
      })
    : state.stages;

  return {
    ...state,
    trafficChannels: state.trafficChannels ?? [],
    stages,
    step: typeof state.step === "number" && state.step > 1 ? 1 : state.step,
    version: 2,
  };
}

function migrateV2ToV3(state: unknown): unknown {
  if (!isRecord(state)) return state;
  return {
    ...state,
    products: Array.isArray(state.products) ? state.products : [],
    version: 3,
  };
}

function trackingDefaults(channel: UnknownRecord): UnknownRecord {
  const campaign = typeof channel.campaignName === "string" ? channel.campaignName : undefined;
  const intent = typeof channel.intentLevel === "string" ? channel.intentLevel : undefined;
  const funnelPosition = typeof channel.funnelPosition === "string" ? channel.funnelPosition : undefined;
  const trackingConfidencePct =
    typeof channel.trackingConfidencePct === "number" ? channel.trackingConfidencePct : undefined;

  return {
    ...(campaign ? { utmCampaign: campaign } : {}),
    ...(intent ? { audienceTemperature: intent } : {}),
    attributionRole: funnelPosition === "retargeting" ? "retargeting" : channel.attributionRole ?? "first_touch",
    trackingSetup: trackingConfidencePct != null && trackingConfidencePct > 0
      ? channel.trackingSetup ?? "utm_only"
      : channel.trackingSetup ?? "none",
    device: channel.device ?? "all",
    rampType: channel.rampType ?? "flat",
    confidence: channel.confidence ?? (
      trackingConfidencePct != null && trackingConfidencePct >= 75
        ? "high"
        : trackingConfidencePct != null && trackingConfidencePct < 40
          ? "low"
          : "medium"
    ),
  };
}

function migrateTrafficChannels(value: unknown): unknown {
  if (!Array.isArray(value)) return [];
  return value.map((channel) => {
    if (!isRecord(channel)) return channel;
    return {
      ...trackingDefaults(channel),
      ...channel,
    };
  });
}

function migrateStages(value: unknown): unknown {
  if (!Array.isArray(value)) return [];
  return value.map((stage) => {
    if (!isRecord(stage)) return stage;
    return {
      requiredStage: true,
      assetStatus: stage.publishedStatus === "live" ? "live" : "draft",
      assetExecutionStatus: "unassigned",
      trackingStatus: "not_set",
      waitDays: 0,
      dueInDays: stage.dueInDays,
      priority: "medium",
      actualPeriodDays: 30,
      ...stage,
    };
  });
}

function migrateProducts(value: unknown): unknown {
  if (!Array.isArray(value)) return [];
  return value.map((product) => {
    if (!isRecord(product)) return product;
    return {
      offerRole: product.isUpsell === true ? "upsell" : "core",
      attachedStageIds: [],
      checkoutType: "one_step",
      productSalesSplitPct: 100,
      orderBumpEnabled: false,
      merchantFixedFee: DEFAULT_REGIONAL_SETTINGS.defaultMerchantFixedFee,
      fulfillmentCostAmount: 0,
      fulfillmentCostType: "none",
      ...product,
    };
  });
}

function migrateExpenses(state: UnknownRecord): unknown[] {
  if (Array.isArray(state.expenses)) {
    return state.expenses.map((expense) => {
      if (!isRecord(expense)) return expense;
      return {
        category: "general",
        frequency: "monthly",
        startMonth: 1,
        ...expense,
      };
    });
  }

  const legacyOperatingExpense =
    typeof state.operatingExpenses === "number" ? state.operatingExpenses : 0;
  if (legacyOperatingExpense > 0) {
    return [{
      id: "legacy-operating-expense",
      label: "General operating expense",
      category: "general",
      amount: legacyOperatingExpense,
      frequency: "monthly",
      startMonth: 1,
    }];
  }
  return [];
}

function migrateGraph(value: unknown): unknown {
  if (!isRecord(value)) return value;
  return {
    ...value,
    nodes: Array.isArray(value.nodes)
      ? value.nodes.map((node) => {
          if (!isRecord(node)) return node;
          const data = isRecord(node.data) ? node.data : {};
          return {
            ...node,
            data: {
              publishedStatus: "draft",
              assetStatus: "draft",
              trackingStatus: "not_set",
              waitDays: 0,
              dueInDays: data.dueInDays,
              requiredStage: true,
              actualPeriodDays: 30,
              assetExecutionStatus: "unassigned",
              ...data,
            },
          };
        })
      : [],
    edges: Array.isArray(value.edges)
      ? value.edges.map((edge) => {
          if (!isRecord(edge)) return edge;
          return {
            edgeType: "direct",
            splitPercent: 100,
            waitDays: 0,
            ...edge,
          };
        })
      : [],
  };
}

function applyV4Defaults(state: unknown): unknown {
  if (!isRecord(state)) return state;
  return {
    ...state,
    regionalSettings: isRecord(state.regionalSettings) ? state.regionalSettings : DEFAULT_REGIONAL_SETTINGS,
    audienceGoalDetails: isRecord(state.audienceGoalDetails) ? state.audienceGoalDetails : {},
    trafficChannels: migrateTrafficChannels(state.trafficChannels),
    stages: migrateStages(state.stages),
    graph: state.graph == null ? undefined : migrateGraph(state.graph),
    products: migrateProducts(state.products),
    scenarios: Array.isArray(state.scenarios) ? state.scenarios : [],
    experiments: Array.isArray(state.experiments) ? state.experiments : [],
    version: 4,
  };
}

function applyV5Defaults(state: unknown): unknown {
  if (!isRecord(state)) return state;
  const base = {
    ...state,
    regionalSettings: isRecord(state.regionalSettings) ? state.regionalSettings : DEFAULT_REGIONAL_SETTINGS,
    audienceGoalDetails: isRecord(state.audienceGoalDetails) ? state.audienceGoalDetails : {},
    trafficChannels: migrateTrafficChannels(state.trafficChannels),
    stages: migrateStages(state.stages),
    products: migrateProducts(state.products),
    expenses: migrateExpenses(state),
    scenarios: Array.isArray(state.scenarios) ? state.scenarios : [],
    experiments: Array.isArray(state.experiments) ? state.experiments : [],
    version: 5,
  };

  return {
    ...base,
    graph: state.graph == null
      ? buildInitialGraph(base as FunnelWizardState)
      : migrateGraph(state.graph),
  };
}

function applyV6Defaults(state: unknown): unknown {
  if (!isRecord(state)) return state;
  return {
    ...state,
    packageRecommendation: { ...DEFAULT_PACKAGE_RECOMMENDATION },
    seasonalDemand: { ...DEFAULT_SEASONAL_DEMAND },
    calculationReceipt: DEFAULT_CALCULATION_RECEIPT,
    version: 6,
  };
}

/**
 * v6 -> v7 widens the three inert slots into real session-only shapes.
 *
 * The migration is strict in the direction that matters: it always resets the
 * three fields to their inert defaults rather than carrying a stored value
 * forward. A v3-v6 draft, an existing share link, or a stored shared plan
 * therefore arrives at v7 with the new functionality disabled and no-op, and a
 * payload that somehow contains v7 data cannot smuggle it into the session.
 * The fields become non-default only when the visitor uses the feature in the
 * live session.
 */
function applyV7Defaults(state: unknown): unknown {
  if (!isRecord(state)) return state;
  return {
    ...state,
    packageRecommendation: { ...DEFAULT_PACKAGE_RECOMMENDATION },
    seasonalDemand: { ...DEFAULT_SEASONAL_DEMAND },
    calculationReceipt: DEFAULT_CALCULATION_RECEIPT,
    version: CURRENT_VERSION,
  };
}

const MIGRATIONS: Record<number, (state: unknown) => unknown> = {
  1: migrateV1ToV2,
  2: migrateV2ToV3,
  3: applyV4Defaults,
  4: applyV5Defaults,
  5: applyV6Defaults,
  6: applyV7Defaults,
};

export function migrateFunnelWizardState(
  state: unknown,
  storedVersion?: number,
): FunnelWizardState | null {
  let current = state;
  const explicitVersion = typeof storedVersion === "number" && Number.isInteger(storedVersion)
    ? storedVersion
    : null;
  let version: number = explicitVersion ?? versionFromState(state) ?? CURRENT_VERSION;

  if (version > CURRENT_VERSION || version < 1) return null;

  while (version < CURRENT_VERSION) {
    const migrate = MIGRATIONS[version];
    if (!migrate) return null;
    current = migrate(current);
    const nextVersion = version + 1;
    if (versionFromState(current) !== nextVersion) return null;
    version = nextVersion;
  }

  // Applied unconditionally, including for a state that already claims v7: the
  // session-only slots must never be restored from a stored payload.
  current = applyV7Defaults(current);
  const parsed = FunnelWizardStateSchema.safeParse(current);
  return parsed.success ? parsed.data : null;
}

export function normalizeFunnelWizardState(state: FunnelWizardState): FunnelWizardState {
  return migrateFunnelWizardState(state, versionFromState(state) ?? CURRENT_VERSION) ?? state;
}
